import pick from 'lodash/pick.js';
import uuid from 'uuid/v4.js';

import { handleResponseError } from '@/Helpers.js';
import Model from '@/models/Model.js';
import TermTemplatePeriod from '@/models/term-template/TermTemplatePeriod.js';

export default class TermTemplatePeriodCourseTemplate extends Model {
    static entity = 'term_period_course_template';

    static modelName = 'TermPeriodCourseTemplate';

    static primaryKey = 'uuid';

    static fields() {
        return {
            id: this.number(null).nullable(),
            uuid: this.attr(() => uuid()),
            title: this.string(''),
            course_template_id: this.number(null).nullable(),
            course_code_id: this.number(0),
            term_schedule_template_period_id: this.attr(null),
            start_week: this.number(null).nullable(),
            start_day: this.number(null).nullable(),
            duration: this.number(null).nullable(),
            credits: this.number(null).nullable(),
            hours: this.number(null).nullable(),
            order: this.number(0),
            type: this.attr(null),

            ...super.fields(),

            term_template_period: this.belongsTo(TermTemplatePeriod, 'term_schedule_template_period_id'),
        };
    }

    // Defaults
    get modelName() {
        return this.modelName;
    }

    static searchable() {
        return ['title'];
    }

    static resetAll() {
        return this.deleteAll();
    }

    // Utility functions
    static fromPeriodId(termPeriodId) {
        return this.query().where('term_schedule_template_period_id', termPeriodId).get();
    }

    // API - GET
    static async fetch(id, termTemplatePeriodId) {
        const endpoint = `/api/term-template-periods/${termTemplatePeriodId}/courses/${id}`;
        try {
            const { response } = await this.api().get(endpoint);
            return response;
        } catch (e) {
            handleResponseError(e);
        }

        return false;
    }

    // API - POST
    static async syncNewCourses(termTemplatePeriod, courses) {
        const endpoint = `/api/term-template-periods/${termTemplatePeriod.id}/courses`;

        try {
            const { response } = await TermTemplatePeriodCourseTemplate.api().post(endpoint, {
                courses,
            });

            if (response.status >= 200 && response.status < 300) {
                this.store().dispatch('AutoSaveEventBus/updateSubscription', {
                    subscription: 'UPDATE_COMPONENT_KEY',
                    data: {
                        action: 'termPeriodUpdated',
                    },
                });

                this.store().dispatch('AutoSaveEventBus/updateSubscription', {
                    subscription: 'UPDATE_COMPONENT_KEY',
                    data: {
                        action: 'termTemplateUpdated',
                    },
                });

                return true;
            }
        } catch (e) {
            handleResponseError(e);
        }

        return false;
    }

    // API - PUT
    static async sync(termPeriodCourse, data) {
        const endpoint = `/api/term-template-periods/${termPeriodCourse.term_schedule_template_period_id}/courses/${termPeriodCourse.id}`;

        try {
            const { response } = await this.api().put(endpoint, data);

            if (response.status >= 200 && response.status < 300) {
                this.store().dispatch('AutoSaveEventBus/updateSubscription', {
                    subscription: 'UPDATE_COMPONENT_KEY',
                    data: {
                        action: 'termPeriodUpdated',
                        termPeriodId: termPeriodCourse.term_schedule_template_period_id,
                    },
                });

                this.store().dispatch('AutoSaveEventBus/updateSubscription', {
                    subscription: 'UPDATE_COMPONENT_KEY',
                    data: {
                        action: 'termTemplateUpdated',
                    },
                });

                return response;
            }
        } catch (e) {
            handleResponseError(e);
        }

        return false;
    }

    // API - DELETE
    static async remove(termPeriodCourse) {
        const endpoint = `/api/term-template-periods/${termPeriodCourse.term_schedule_template_period_id}/courses/${termPeriodCourse.id}`;
        try {
            const { response } = await this.api().delete(endpoint);

            if (response.status === 200) {
                await this.delete(termPeriodCourse.uuid);

                this.store().dispatch('AutoSaveEventBus/updateSubscription', {
                    subscription: 'UPDATE_COMPONENT_KEY',
                    data: {
                        action: 'termTemplateUpdated',
                    },
                });

                return response;
            }
        } catch (e) {
            handleResponseError(e);
        }

        return false;
    }

    static async addNewCourses(termTemplatePeriod, courses) {
        const requestData = await Promise.all(
            courses.map(async (course, index) => {
                const structure = {
                    title: course.name,
                    course_code_id: course.id,
                    term_schedule_template_period_id: termTemplatePeriod.id,
                    order: index,
                    type: 'course',
                };

                const newCourse = await new TermTemplatePeriodCourseTemplate(structure).$save();

                return pick(newCourse, [
                    'uuid',
                    'title',
                    'course_code_id',
                    'term_schedule_template_period_id',
                    'order',
                    'type',
                ]);
            })
        );

        await this.syncNewCourses(termTemplatePeriod, requestData);

        return true;
    }

    static async addNewBreak(termTemplatePeriod) {
        const index = TermTemplatePeriodCourseTemplate.query()
            .where('term_schedule_template_period_id', termTemplatePeriod.id)
            .get().length;
        const requestData = [];

        const courseBreak = await new TermTemplatePeriodCourseTemplate({
            title: 'Break',
            course_code_id: null,
            term_schedule_template_period_id: termTemplatePeriod.id,
            order: index,
            type: 'break',
        }).$save();

        requestData.push(
            pick(courseBreak, ['uuid', 'title', 'course_code_id', 'term_schedule_template_period_id', 'order', 'type'])
        );

        await this.syncNewCourses(termTemplatePeriod, requestData);

        return true;
    }

    static async reorder(termPeriodId, updatedCourses) {
        const endpoint = `/api/term-template-periods/${termPeriodId}/courses/reorder`;
        try {
            const { response } = await this.api().post(endpoint, {
                courses: updatedCourses,
            });

            if (response.status >= 200 && response.status < 300) {
                this.store().dispatch('AutoSaveEventBus/updateSubscription', {
                    subscription: 'UPDATE_COMPONENT_KEY',
                    data: {
                        action: 'termPeriodUpdated',
                        termPeriodId,
                    },
                });

                await this.store().dispatch('AutoSaveEventBus/updateSubscription', {
                    subscription: 'UPDATE_COMPONENT_KEY',
                    data: {
                        action: 'termTemplateUpdated',
                    },
                });

                return response;
            }
        } catch (e) {
            handleResponseError(e);
        }

        return false;
    }
}
